/**
 * Resolve the instance Root tax id (CNPJ/CPF) for inbound PIX origin lock.
 *
 * Same source as Baas Root page / IssuerFundingOriginResolver.InferOwnerDocumentAsync:
 * 1) Owner entity registration document
 * 2) RootAccountId account's linked entity document (when owner is missing)
 *
 * Does not invent values — returns "" when APIs have no CPF/CNPJ.
 */

import { fetchBaasAccountById } from "./baasAccountsListApi";
import { fetchBaasEntityById } from "./baasEntidadesApi";
import { fetchBaasInstanceDetail } from "./baasInstancesApi";
import { resolveBaasInstanceOwnerEntityId } from "./baasVirtualShells";

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const isCpfOrCnpj = (digits) => {
  const d = digitsOnly(digits);
  return d.length === 11 || d.length === 14;
};

/** Format CPF/CNPJ for console inputs (digits-only when length is unexpected). */
export const formatBaasTaxDocument = (value) => {
  const digits = digitsOnly(value);
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5",
    );
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  }
  return digits || String(value || "").trim();
};

/**
 * @param {object|null|undefined} instance selected Baas instance (or detail)
 * @returns {Promise<string>} digits-only CPF/CNPJ, or ""
 */
export const resolveBaasInstanceRootDocument = async (instance) => {
  if (!instance) return "";

  let ownerEntityId = resolveBaasInstanceOwnerEntityId(instance);
  let rootAccountId = String(
    instance.rootAccountId ?? instance.root_account_id ?? "",
  ).trim();

  if (!ownerEntityId || !rootAccountId) {
    try {
      const detail = await fetchBaasInstanceDetail(instance);
      ownerEntityId = resolveBaasInstanceOwnerEntityId(detail) || ownerEntityId;
      rootAccountId = String(
        detail?.rootAccountId ?? detail?.root_account_id ?? rootAccountId,
      ).trim();
    } catch {
      // Keep whatever we already have from the selection.
    }
  }

  if (ownerEntityId) {
    try {
      const entity = await fetchBaasEntityById(instance, ownerEntityId);
      const digits = digitsOnly(entity?.document);
      if (isCpfOrCnpj(digits)) return digits;
    } catch {
      // Fall through to RootAccountId.
    }
  }

  // Skip synthetic VIRTUAL_ROOT ids (INST-…-VIRTUAL_ROOT) — no tax id there.
  if (rootAccountId && !/VIRTUAL_ROOT/i.test(rootAccountId) && !/^INST-/i.test(rootAccountId)) {
    try {
      const rootAccount = await fetchBaasAccountById(instance, rootAccountId);
      const entityId = String(rootAccount?.entityId || "").trim();
      if (entityId) {
        const entity = await fetchBaasEntityById(instance, entityId).catch(() => null);
        const fromEntity = digitsOnly(entity?.document);
        if (isCpfOrCnpj(fromEntity)) return fromEntity;
      }
      const fromAccount = digitsOnly(
        rootAccount?.document
          ?? rootAccount?.nationalRegistration
          ?? rootAccount?.national_registration
          ?? rootAccount?.raw?.national_registration
          ?? rootAccount?.raw?.nationalRegistration
          ?? "",
      );
      if (isCpfOrCnpj(fromAccount)) return fromAccount;
    } catch {
      // No invent — return empty.
    }
  }

  return "";
};
